import type { Writable } from "svelte/store";
import type { CloudinaryConfig } from "./fs";
import { FS } from "./fs";
import { FsDir } from "./fsentry";
import type { HttpProgress } from "./http";
import { mkURL } from "./http";
import { throwIf } from "./util";

// Cloudinary's Admin API does not answer other origins, and the API secret
// must not be used from a page. Calls go through /api/cloudinary. The cloud
// name, the API key and the API secret are request headers.
//
// A file is an asset in an asset folder. Cloudinary keeps the format of an
// image or a video apart from its name, so such a file cannot be renamed to
// another extension. It does not store empty files.

export const prop_cloudinary_url = "cloudinary_url";
export const prop_cloudinary_public_id = "cloudinary_public_id";
export const prop_cloudinary_type = "cloudinary_type";
export const prop_cloudinary_format = "cloudinary_format";

class FileSystemCloudinary extends FS {
  supportsDirRename = true;

  constructor(config: CloudinaryConfig) {
    super(config);
  }

  get cloudinaryConfig(): CloudinaryConfig {
    return this.config as CloudinaryConfig;
  }

  async readDir(dirPath: string, force: boolean, progress: Writable<any>): Promise<FsDir> {
    throwIf(dirPath == "", "empty dir");
    return this.loadDir(dirPath, force, progress, async () => {
      let js = await cloudinaryCall(this.cloudinaryConfig, mkURL("/api/cloudinary/list", "dir", dirPath));
      if (!Array.isArray(js)) {
        throw new Error("cloudinary list failed");
      }
      return FsDir.fromWire(dirPath, js);
    });
  }

  // The address Cloudinary delivers the file at.
  async getPublicURL(path: string): Promise<string> {
    let js = await cloudinaryCall(this.cloudinaryConfig, mkURL("/api/cloudinary/link", "path", path));
    throwIf(typeof js.url != "string" || js.url == "", "cloudinary gave no link");
    return js.url;
  }

  async downloadFile(path: string, httpProgress: HttpProgress): Promise<Blob> {
    let url = mkURL("/api/cloudinary/download", "path", path);
    let xhr = await cloudinarySend("GET", url, this.cloudinaryConfig, undefined, httpProgress, "blob");
    return xhr.response as Blob;
  }

  async uploadFile(path: string, file: Blob, httpProgress: HttpProgress): Promise<void> {
    let url = mkURL("/api/cloudinary/upload", "path", path);
    await cloudinarySend("PUT", url, this.cloudinaryConfig, file, httpProgress);
  }

  async rename(path: string, newPath: string): Promise<void> {
    throwIf(path == newPath, "path must be != newPath");
    let url = mkURL("/api/cloudinary/rename", "path", path, "newPath", newPath);
    await cloudinaryCall(this.cloudinaryConfig, url, "POST");
  }

  async createDirectory(dirPath: string): Promise<void> {
    await cloudinaryCall(this.cloudinaryConfig, mkURL("/api/cloudinary/mkdir", "path", dirPath), "POST");
  }

  // A folder goes with everything in it.
  async deleteFile(path: string): Promise<void> {
    await cloudinaryCall(this.cloudinaryConfig, mkURL("/api/cloudinary/delete", "path", path), "POST");
  }
}

export function cloudinaryHeaders(config: CloudinaryConfig): Record<string, string> {
  return {
    "X-Cloudinary-Cloud": config.cloudName,
    "X-Cloudinary-Key": config.apiKey,
    "X-Cloudinary-Secret": config.apiSecret,
  };
}

export async function testCloudinaryConnection(config: CloudinaryConfig): Promise<void> {
  await cloudinaryCall(config, "/api/cloudinary/test");
}

async function cloudinaryCall(config: CloudinaryConfig, url: string, method = "GET"): Promise<any> {
  let rsp = await fetch(url, {
    method,
    headers: cloudinaryHeaders(config),
  });
  let text = await rsp.text();
  let js: any = {};
  if (text) {
    try {
      js = JSON.parse(text);
    } catch {
      js = {};
    }
  }
  if (!rsp.ok) {
    throw new Error(js.error || `cloudinary ${rsp.status}`);
  }
  return js;
}

function cloudinarySend(
  method: string,
  url: string,
  config: CloudinaryConfig,
  data: Blob | undefined,
  onProgress: HttpProgress | undefined,
  responseType?: XMLHttpRequestResponseType,
): Promise<XMLHttpRequest> {
  return new Promise((resolve, reject) => {
    let xhr = new XMLHttpRequest();
    xhr.open(method, url);
    if (responseType) {
      xhr.responseType = responseType;
    }
    for (let [k, v] of Object.entries(cloudinaryHeaders(config))) {
      xhr.setRequestHeader(k, v);
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(xhr);
        return;
      }
      cloudinaryErrText(xhr).then(
        (msg) => reject(new Error(msg)),
        () => reject(new Error(`cloudinary ${xhr.status}`)),
      );
    };
    xhr.onerror = () => {
      reject(new Error("cloudinary request failed"));
    };
    if (onProgress) {
      let started = Date.now();
      let report = (ev: ProgressEvent) => {
        let total = ev.lengthComputable ? ev.total : 0;
        onProgress(ev.loaded, total, Date.now() - started);
      };
      if (method == "PUT") {
        xhr.upload.onprogress = report;
      } else {
        xhr.onprogress = report;
      }
    }
    if (data) {
      xhr.send(data);
    } else {
      xhr.send();
    }
  });
}

async function cloudinaryErrText(xhr: XMLHttpRequest): Promise<string> {
  let text = "";
  if (xhr.responseType == "blob" && xhr.response instanceof Blob) {
    text = await xhr.response.text();
  } else if (xhr.responseType == "" || xhr.responseType == "text") {
    text = xhr.responseText || "";
  }
  if (text) {
    try {
      let js = JSON.parse(text);
      if (js.error) {
        return String(js.error);
      }
    } catch {
      // keep the status
    }
  }
  return `cloudinary ${xhr.status}`;
}

export function makeFileSystemCloudinary(config: CloudinaryConfig): FileSystemCloudinary {
  return new FileSystemCloudinary(config);
}
